import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { When } from 'react-if';
import { safeLocalStorage } from '@toss/storage';
import { overlay } from 'overlay-kit';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '@zep/core-logger';
import { type QuizGenerateItem } from '@/api/ai/type';
import type { WebsiteData } from '@/api/ai/website/types';
import type { YoutubeVideoData } from '@/api/ai/youtube/types';
import { useLocale } from '@/hooks';
import { useCountryCode, useUnmount } from '@/shared/hooks';
import { useUserPlanManager } from '@/shared/plan/UserPlanManagerProvider';
import { useUpsellingGuard } from '@/shared/plan/useUpsellingGuard';
import { AttachmentProvider } from '@/views/build/features/ai-prompt-attachment/components/AttachmentProvider';
import { PromptInput } from '@/views/build/features/ai-prompt-attachment/components/PromptInput';
import { BuildSimplePrompt } from '@/views/build/features/ai-prompt-input/components/BuildAiSimplePrompt';
import { PromptOptionsSection } from '@/views/build/features/ai-prompt-input/components/PromptOptionsSection';
import { PromptTextArea } from '@/views/build/features/ai-prompt-input/components/PromptTextArea';
import { getPromptMaxLength } from '@/views/build/features/ai-prompt-input/constants/promptMaxLength.const';
import { useAiBuild } from '@/views/build/features/ai-quiz-generation/hooks/useAiBuild';
import { useAiTrialStatus } from '@/views/build/features/ai-quiz-generation/hooks/useAiTrialStatus';
import { getPendingFallback } from '@/views/build/features/ai-quiz-generation/utils/getPendingFallback';
import { StandardCodeRecommendation } from './components/StandardCodeRecommendation';
import { type BuildAiCategoryContextValue, BuildAiCategoryProvider } from './context/BuildAiCategoryProvider';
import { useAiModalState } from './hooks/useAiModalState';
import { useAttachmentUpsellingGuards } from './hooks/useAttachmentUpsellingGuards';
import { useAutoTrigger } from './hooks/useAutoTrigger';
import { useStandardRecommendation } from './hooks/useStandardRecommendation';
import { BuildAiModalBase } from './BuildAiModalBase';
import { type BuildAiModalOpenParams, type BuildAiModalProps, DEFAULT_FEATURES, DEFAULT_GENERATION_MODE, type GenerationMode, type SelectedMode } from './types';

// v1 키는 default 가 'fast' 였던 시절의 사용자 선택을 담고 있어 그대로 읽으면
// 신규 default('thorough')가 적용되지 않는다. v2 로 옮긴 뒤 v1 키는 모달 마운트
// 시점에 best-effort 로 정리한다 (값 자체는 migrate 하지 않음).
const STORED_GENERATION_MODE_KEY_V1_DEPRECATED = 'quiz.buildAi.generationMode';
const STORED_GENERATION_MODE_KEY_V2 = 'quiz.buildAi.generationMode.v2';
const readStoredGenerationMode = (): GenerationMode => {
  const stored = safeLocalStorage.get(STORED_GENERATION_MODE_KEY_V2);
  return stored === 'fast' || stored === 'thorough' ? stored : DEFAULT_GENERATION_MODE;
};
const persistGenerationMode = (mode: GenerationMode) => {
  safeLocalStorage.set(STORED_GENERATION_MODE_KEY_V2, mode);
};
const BuildAiModalComponent = ({
  onClose,
  onSave,
  quizCountLimit,
  type = 'none',
  features = DEFAULT_FEATURES,
  triggerSource = 'manual',
  initialGenerationId,
  uiCohort = 'as_is'
}: BuildAiModalProps) => {
  // Modal state management
  const modalState = useAiModalState();
  const upsellingGuard = useUpsellingGuard();
  const {
    isKR
  } = useCountryCode();
  const {
    locale
  } = useLocale();
  const promptMaxLength = useMemo(() => getPromptMaxLength(locale), [locale]);

  // 쿼터 제한 상태 관리 (guest / FREE plan)
  const {
    isQuotaLimited,
    remaining,
    lifetimeLimit,
    isQuotaExhausted,
    decrementRemaining,
    isLoading: isLoadingGuestTrial
  } = useAiTrialStatus();

  // Standards management (kept in modal - ADR-3)
  const {
    selectedAchievementStandards,
    handleAdd: handleAddStandards,
    handleRemove: handleRemoveStandard
  } = useStandardRecommendation();

  // 꼼꼼하게 모드는 BASIC 이상 플랜에서만 사용 가능. FREE 유저에게는 dropdown 에
  // PlanBadge(basic) 가 노출되고, 선택 시 업셀링 모달이 열린다.
  const {
    userPlanManager
  } = useUserPlanManager();
  const canAccessThorough = userPlanManager.canAccessFeature('AI_GENERATION_THOROUGH');
  const lockedModes = useMemo<readonly GenerationMode[]>(() => canAccessThorough ? [] : ['thorough'], [canAccessThorough]);

  // 빠르게/꼼꼼하게 모드 선택 상태. 백엔드 동작이 추가되기 전까지는 UI/분석 트래킹용.
  // 모달 진입 시 localStorage 에 저장된 직전 선택을 default 로 사용하되, thorough
  // 접근 권한이 없는 유저(예: FREE)는 fast 로 보정한다. handleModeChange 의
  // upsellingGuard 는 dropdown 능동 선택 경로만 커버하므로 default 가 'thorough'
  // 그대로면 메인 생성 버튼이 잠긴 모드로 요청을 보내는 회귀가 생긴다. v2 저장값은
  // 손대지 않아 plan 업그레이드 시 thorough 선호가 자연 복구된다.
  const [generationMode, setGenerationMode] = useState<GenerationMode>(() => {
    const stored = readStoredGenerationMode();
    if (stored === 'thorough' && !canAccessThorough) return 'fast';
    return stored;
  });
  const shouldShowGenerationModeDropdown = uiCohort === 'dropdown';
  const effectiveGenerationMode = shouldShowGenerationModeDropdown ? generationMode : undefined;
  // spec §4.2: as_is 는 'default', dropdown 은 유저 선택값.
  const selectedMode: SelectedMode = shouldShowGenerationModeDropdown ? generationMode : 'default';
  const {
    attachment,
    form,
    generation
  } = useAiBuild({
    onSuccess: () => {
      modalState.handleGenerationSuccess();
      decrementRemaining();
    },
    selectedAchievementStandards: features.achievements.enabled ? selectedAchievementStandards : [],
    initialPrompt: features.initialPrompt,
    topicId: features.topicId,
    initialGenerationId,
    // dropdown cohort 만 값 전달. as_is 는 undefined → 서버 기본 모델 경로.
    generationMode: effectiveGenerationMode,
    uiCohort,
    selectedMode
  });
  const openedAtRef = useRef(Date.now());
  const hasSavedRef = useRef(false);
  const hasExposedDropdownRef = useRef(false);
  const previousModeRef = useRef<GenerationMode>(generationMode);
  // 노출 이벤트의 default_mode 는 "사용자가 처음 본 모드" 이므로 첫 mount 시점의
  // generationMode 를 ref 로 스냅샷해서 사용한다. 이후 selection 변화는 무시.
  const initialGenerationModeRef = useRef<GenerationMode>(generationMode);

  // 마이그레이션 정리: v1 키가 남아있는 유저의 localStorage 에서 best-effort 로 제거.
  // 모달이 열릴 때 1회 수행하면 충분하며, 키가 없어도 safeLocalStorage.remove 는 no-op.
  useEffect(() => {
    safeLocalStorage.remove(STORED_GENERATION_MODE_KEY_V1_DEPRECATED);
  }, []);

  // dropdown 코호트가 split button 을 본 첫 순간 1회만 emit (assignment ⊇ exposure).
  // default_mode 는 spec 상 default(DEFAULT_GENERATION_MODE) 가 아니라 plan 보정 후
  // 사용자가 실제로 본 초기값(initialGenerationModeRef) 을 보낸다. 그래야 FREE 보정
  // 으로 fast 가 노출된 케이스도 분석에서 thorough 로 잘못 집계되지 않는다. 초기값
  // 만 필요하므로 generationMode 자체는 deps 에 넣지 않는다 (selection 변화로 effect
  // 재실행되는 노이즈 회피).
  useEffect(() => {
    if (!shouldShowGenerationModeDropdown) return;
    if (hasExposedDropdownRef.current) return;
    hasExposedDropdownRef.current = true;
    logger.track('biz_generation_mode_dropdown_exposed', {
      generation_id: generation.generationId,
      ui_cohort: 'dropdown',
      default_mode: initialGenerationModeRef.current,
      can_access_thorough: canAccessThorough,
      locked_modes: lockedModes
    });
  }, [shouldShowGenerationModeDropdown, generation.generationId, canAccessThorough, lockedModes]);

  // 모든 close 경로에서 단 1회 발화. 직전 구현은 handleClose 경로에서만 emit해서
  // BuildPageInitializer 저장 후 auto-close, useOpenBuildAiModal max 도달 unmount 케이스에서 누락.
  useUnmount(() => {
    logger.track('biz_ai_generation_modal_closed', {
      generation_id: generation.generationId,
      ui_cohort: uiCohort,
      has_generated_items: generation.items.length > 0,
      generated_question_count: generation.items.length,
      closed_without_save: !hasSavedRef.current,
      elapsed_ms_from_open: Date.now() - openedAtRef.current
    });
  });
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Attachment 초기화 ref guard -- React strict mode에서 이중 실행 방지
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current && type === 'youtube') {
      hasInitialized.current = true;
      void attachment.handleAttachYoutube();
    }
    if (!hasInitialized.current && type === 'website') {
      hasInitialized.current = true;
      void attachment.handleAttachWebsite();
    }
  }, [type, attachment]);

  // Auto-trigger generation on mount (landing flow)
  useAutoTrigger({
    enabled: features.autoTrigger,
    onTrigger: generation.handleGenerate
  });

  // Simple mode prompt summary
  const promptSummary = useMemo(() => {
    if (attachment.state.type === 'file') {
      return (attachment.state.data as File)?.name ?? '';
    }
    if (attachment.state.type === 'youtube') {
      return (attachment.state.data as YoutubeVideoData)?.title ?? '';
    }
    if (attachment.state.type === 'website') {
      return (attachment.state.data as WebsiteData)?.title ?? '';
    }
    return form.textPrompt;
  }, [attachment.state, form.textPrompt]);

  // Attachment upselling guards (plan-based)
  const {
    beforeFileAttach,
    handleYoutube,
    handleWebsite
  } = useAttachmentUpsellingGuards(attachment.handleAttachYoutube, attachment.handleAttachWebsite);
  const handleSave = useCallback(async (quizzes: QuizGenerateItem[]) => {
    // saved 신호를 onSave 호출 전 set한다. onSave 내부에서 모달을 먼저 닫는
    // 경로(BuildPageInitializer.handleSave의 buildAiModal.close(),
    // useOpenBuildAiModal의 overlay.unmount)에서 unmount cleanup이 await suspend
    // 사이에 끼어들어 closed_without_save=true로 잘못 기록되는 race 회피.
    hasSavedRef.current = true;

    // onSave가 false를 반환하면(예: useOpenBuildAiModal의 max 가드) 실제 저장이
    // 거부된 것이므로 saved 플래그를 롤백하고 saved 분석 이벤트는 발화하지 않는다.
    // 가드 경로는 모달을 닫지 않으므로(overlay.unmount 미호출) 롤백 시점에 cleanup
    // 이 아직 발화하지 않았고, 이후 정상 close 경로에서 closed_without_save=true로
    // 정확히 기록된다. void/true 반환은 성공으로 취급(backward-compat).
    const result = await onSave(quizzes, generation.generationId);
    if (result === false) {
      hasSavedRef.current = false;
      return;
    }
    const payload = {
      selectedCount: quizzes.length,
      totalGenerated: generation.items.length,
      generation_id: generation.generationId,
      ...(triggerSource === 'landing' && {
        trigger_source: 'landing'
      })
    };
    logger.track('ai_문제생성_저장', payload);
    logger.track('biz_ai_generation_saved', {
      ...payload,
      ui_cohort: uiCohort,
      selected_mode: selectedMode,
      saved_count: quizzes.length,
      generated_count: generation.items.length
    });
  }, [generation.items.length, generation.generationId, onSave, triggerSource, uiCohort, selectedMode]);
  const handleGenerateWithMode = useCallback(() => {
    if (!generation.handleGenerate) return;
    logger.track('biz_ai_generation_mode_used', {
      generation_id: generation.generationId,
      ui_cohort: 'dropdown',
      selected_mode: generationMode,
      attachment_type: type,
      quiz_type: form.options.quizType,
      quiz_count: form.options.quizCount,
      difficulty: form.options.quizLevel
    });
    generation.handleGenerate();
  }, [generation, generationMode, type, form.options.quizType, form.options.quizCount, form.options.quizLevel]);
  const handleModeChange = useCallback((mode: GenerationMode) => {
    const applyChange = () => {
      const fromMode = previousModeRef.current;
      previousModeRef.current = mode;
      setGenerationMode(mode);
      persistGenerationMode(mode);
      logger.track('biz_ai_generation_mode_changed', {
        generation_id: generation.generationId,
        ui_cohort: 'dropdown',
        from_mode: fromMode,
        to_mode: mode,
        is_locked: lockedModes.includes(mode),
        // 권한 있으면 thorough 선택해도 upsell 모달 안 뜸.
        upsell_shown: mode === 'thorough' && !canAccessThorough
      });
    };
    // 잠긴 모드 선택 시 useUpsellingGuard 가 권한 없으면 업셀링 모달 노출.
    if (mode === 'thorough') {
      upsellingGuard(applyChange, {
        feature: 'AI_GENERATION_THOROUGH',
        from: 'ai_generation_thorough'
      });
      return;
    }
    applyChange();
  }, [generation.generationId, upsellingGuard, lockedModes, canAccessThorough]);
  const resolvedOnGenerate = (() => {
    if (!generation.handleGenerate) return undefined;
    if (shouldShowGenerationModeDropdown) return handleGenerateWithMode;
    return generation.handleGenerate;
  })();
  return <BuildAiModalBase actions={{
    onClose: handleClose,
    onGenerate: resolvedOnGenerate,
    onSave: (quizzes: QuizGenerateItem[]) => {
      void handleSave(quizzes);
    },
    onDelete: generation.handleDeleteItem
  }} generationMode={shouldShowGenerationModeDropdown ? {
    selected: generationMode,
    onChange: handleModeChange,
    locked: lockedModes,
    // 인풋에 텍스트가 입력되어 있을 때만 coach mark 노출 트리거.
    // dismiss 영구화 + idle 상태 한정은 SplitButton 내부에서 처리.
    shouldShowCoachMark: generation.status === 'idle' && form.textPrompt.length > 0
  } : undefined} content={{
    pendingFallback: getPendingFallback(attachment.state, form.textPrompt, form.options.quizType, selectedAchievementStandards.length > 0),
    promptContent: modalState.shouldShowSimple ? <BuildSimplePrompt prompt={promptSummary} onShowFullPromptClick={modalState.showFullPrompt} /> : <div className="flex flex-col gap-lg pt-md">
            <PromptTextArea.Label attachmentType={attachment.state.type} value={form.textPrompt} maxLength={promptMaxLength}>
              <AttachmentProvider attachment={attachment.state} onAttachFile={attachment.handleAttachFile} onAttachYoutube={handleYoutube} onAttachWebsite={handleWebsite} onDetach={attachment.handleDetach} generateStatus={generation.status} autoOpenFilePicker={type === 'file'} beforeAttach={beforeFileAttach}>
                <PromptInput prompt={form.textPrompt} onChangePrompt={form.onChangePrompt} maxLength={promptMaxLength} />
              </AttachmentProvider>
              <When condition={isKR}>
                <StandardCodeRecommendation shouldButtonSimple={generation.status !== 'idle'} selectedAchievementStandards={selectedAchievementStandards} onAddStandards={() => upsellingGuard(() => handleAddStandards({
            attachmentState: attachment.state,
            textPrompt: form.textPrompt
          }), {
            feature: 'AI_ACHIEVEMENT_STANDARD',
            from: 'ai_achievement_standard'
          })} onRemoveStandard={handleRemoveStandard} />
              </When>
            </PromptTextArea.Label>
            <PromptOptionsSection options={form.options} onOptionsChange={form.onChangeOptions} />
          </div>
  }} generation={{
    status: generation.status,
    items: generation.items,
    quizCount: form.options.quizCount
  }} guestTrial={{
    isQuotaLimited,
    remaining,
    lifetimeLimit,
    isQuotaExhausted,
    isLoading: isLoadingGuestTrial
  }} quizCountLimit={quizCountLimit} selectedAchievementStandards={selectedAchievementStandards} data-sentry-element="BuildAiModalBase" data-sentry-component="BuildAiModalComponent" data-sentry-source-file="BuildAiModal.tsx" />;
};
export const buildAiModalOverlayId = uuidv4();
export const buildAiModal = {
  open: ({
    onSave,
    quizCountLimit,
    type,
    categoryProviderValue,
    features = DEFAULT_FEATURES,
    triggerSource = 'manual',
    uiCohort
  }: BuildAiModalOpenParams & {
    categoryProviderValue?: BuildAiCategoryContextValue;
  }) => {
    const generationId = uuidv4();
    // 'floating' 은 진입 트리거이지 attachment 가 아니라서 'none' 으로 정규화.
    const attachmentType = type === 'floating' ? 'none' : type;
    const from = attachmentType === 'none' ? 'query' : attachmentType;
    logger.track('ai_문제생성_오픈', {
      from,
      generation_id: generationId
    });
    logger.track('biz_ai_generation_modal_opened', {
      from,
      generation_id: generationId,
      ui_cohort: uiCohort ?? 'as_is',
      trigger_source: triggerSource,
      attachment_type: attachmentType
    });
    return overlay.open(({
      unmount
    }) => {
      return <BuildAiCategoryProvider value={categoryProviderValue}>
            <BuildAiModalComponent type={attachmentType} onClose={unmount} onSave={onSave} quizCountLimit={quizCountLimit} features={features} triggerSource={triggerSource} initialGenerationId={generationId} uiCohort={uiCohort} />
          </BuildAiCategoryProvider>;
    }, {
      overlayId: buildAiModalOverlayId
    });
  },
  close: () => {
    overlay.unmount(buildAiModalOverlayId);
  }
};